import { ControlProperty, ListRange, SearchCriteriaBase, UserBaseModel } from '../../../../../common/models';
import {
  DEFAULT_PAGE_SIZE,
  EMAIL_PATTERN,
  ApprovalStatusEnum,
  ListQueryParam,
  UnitSystem,
} from '../../../../../common/constants';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { mustNotBeBlankValidator, setStateAllFormControls } from '../../../../../common/utils';
import { UserOwner, UserOwnerReadModel } from './user-owner.model';
import { UserDeactivationSettings, UserDeactivationSettingsForm } from './user-deactivation.model';
import { UserRolesModel } from './user-roles.model';
import { UserOwnerType } from '../constants';
import { AssignableCompartmentList } from '../../../../../container-components/compartments-selection';

export interface UserBrief {
  id?: number;
  active: boolean;
  approved: boolean;
  approvalStatus: ApprovalStatusEnum;
  email: string;
  firstName: string;
  lastName: string;
  owners: UserOwnerReadModel[];
  newsSubscribed: boolean;
}

export interface UserAssignedHub {
  id: number;
  name: string;
}

export class UserEmailSearchModel {
  email: string;
  exist: boolean;
  constructor(params?: UserEmailSearchModel) {
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UserModel extends UserBaseModel {
  owners: UserOwnerReadModel[] = [];
  assignedHubs: UserAssignedHub[] = [];
  assignedCompartments: AssignableCompartmentList[] = [];
  active = true;
  approved = false;
  approvalStatus: ApprovalStatusEnum;
  deactivationSettings: UserDeactivationSettings;

  constructor(params?: UserModel) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UserUpdateModel extends UserModel {
  owner: UserOwner = new UserOwner({ type: UserOwnerType.Organization });
  constructor(params?: UserUpdateModel | UserModel) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}

export class UserOwnerForm {
  constructor(
    model: UserOwner,
    isUserUnderOrganization: boolean,
    public id: ControlProperty<number[]> = [
      { value: model.id, disabled: isUserUnderOrganization },
      [Validators.required],
    ],
    public type: ControlProperty<UserOwnerType> = [model.type, [Validators.required]],
  ) {}
}

export class UserForm {
  id?;
  constructor(
    model: UserUpdateModel,
    hasUpdatePermission: boolean = true,
    public firstName: ControlProperty<string> = [
      { value: model.firstName, disabled: !hasUpdatePermission },
      [Validators.required, mustNotBeBlankValidator],
    ],
    public lastName: ControlProperty<string> = [
      { value: model.lastName, disabled: !hasUpdatePermission },
      [Validators.required, mustNotBeBlankValidator],
    ],
    public active: ControlProperty<boolean> = [
      { value: model.active, disabled: !hasUpdatePermission },
      [Validators.required],
    ],
    public approved: ControlProperty<boolean> = [model.approved, []],
    public email: ControlProperty<string> = [
      {
        value: model.email,
        disabled: !!model.email,
      },
      [Validators.required, Validators.pattern(EMAIL_PATTERN)],
    ],
    public timezone: ControlProperty<string> = [
      { value: model.timezone, disabled: !hasUpdatePermission },
      [Validators.required],
    ],
    public department: ControlProperty<string> = [{ value: model.department, disabled: !hasUpdatePermission }, []],
    public country: ControlProperty<string> = [
      { value: model.country, disabled: !hasUpdatePermission },
      [Validators.required],
    ],
    public roles: ControlProperty<UserRolesModel> = [model.roles as UserRolesModel, [Validators.required]],
    public owner: UntypedFormGroup = new UntypedFormBuilder().group(
      new UserOwnerForm(model.owner, model.owner.type === UserOwnerType.Organization),
    ),
    public assignedHubs: ControlProperty<UserAssignedHub[]> = [{ value: model.assignedHubs, disabled: false }, []],
    public assignedCompartments: ControlProperty<AssignableCompartmentList[]> = [
      { value: model.assignedCompartments, disabled: false },
      [],
    ],
    public defaultLanguage: ControlProperty<string> = [
      { value: model.defaultLanguage, disabled: !hasUpdatePermission },
      [Validators.required],
    ],
    public deactivationSettings: UntypedFormGroup = new UntypedFormBuilder().group(
      new UserDeactivationSettingsForm(new UserDeactivationSettings(model.deactivationSettings)),
    ),
    public unitSystem: ControlProperty<UnitSystem> = [
      //TODO change disabled to !hasUpdatePermission after the last story about unit system
      { value: model.unitSystem, disabled: true },
      [Validators.required],
    ],
  ) {
    if (model.id) {
      this.id = [model.id, []];
    }

    if (!model.deactivationSettings) {
      setStateAllFormControls(this.deactivationSettings, true);
    }
  }
}

export class UserListSearchCriteria extends SearchCriteriaBase {
  [ListQueryParam.Size] = DEFAULT_PAGE_SIZE;
  [ListQueryParam.Page] = 0;
  hasApprovedAdmittances: boolean;
  hasPendingAdmittances: boolean;
  constructor(params?: UserListSearchCriteria | ListRange) {
    super();
    if (params) {
      Object.assign(this, params);
    }
  }
}
